import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useUploadProductUpdateImage } from "../hooks/useProductUpdates";

// Merged per-recipient at send time (AdminProductUpdatesService.personalize) -
// plain string substitution, not real Handlebars, so these work in Subject,
// Preheader, and Body alike.
const VARIABLES = [
  { token: "{{firstName}}", label: "First name" },
  { token: "{{lastName}}", label: "Last name" },
  { token: "{{email}}", label: "Email" },
  { token: "{{phoneNumber}}", label: "Phone number" },
];

const VariableChips = ({ onInsert }) => (
  <div className="flex flex-wrap gap-1.5 items-center mt-1">
    <span className="text-xs text-gray-400">Insert:</span>
    {VARIABLES.map((v) => (
      <button
        key={v.token}
        type="button"
        onClick={() => onInsert(v.token)}
        className="px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full hover:bg-emerald-100"
      >
        {v.label}
      </button>
    ))}
  </div>
);

const buildInitialForm = (campaign) => ({
  subject: campaign?.subject || "",
  preheaderText: campaign?.preheaderText || "",
  bodyHtml: campaign?.bodyHtml || "",
  senderName: campaign?.senderName || "Novacrust Team",
  replyToEmail: campaign?.replyToEmail || "",
});

const ProductUpdateFormModal = ({ isOpen, onClose, onSubmit, isLoading, campaign }) => {
  const [form, setForm] = useState(buildInitialForm(campaign));
  const [testEmail, setTestEmail] = useState("");
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const preheaderRef = useRef(null);
  const { mutateAsync: uploadImage } = useUploadProductUpdateImage();

  // Inserts a {{variable}} token at the current cursor position of a plain
  // text input (Subject/Preheader), same approach as CampaignFormModal's
  // {{cta}} insert - so the admin controls exactly where it lands.
  const insertVariableIntoField = (field, ref, token) => {
    const input = ref.current;
    const current = form[field];
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    handleChange(field, next);
    requestAnimationFrame(() => {
      input?.focus();
      const cursor = start + token.length;
      input?.setSelectionRange(cursor, cursor);
    });
  };

  const insertVariableIntoBody = (token) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range ? range.index : editor.getLength(), token);
  };

  useEffect(() => {
    if (isOpen) setForm(buildInitialForm(campaign));
  }, [isOpen, campaign]);

  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const result = await uploadImage(file);
        const url = result?.data?.url;
        if (!url) return;
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        editor?.insertEmbed(range ? range.index : 0, "image", url);
      } catch (err) {
        // Surfaced via the composer's own error handling at the call site
        // is out of scope here - a failed upload just leaves nothing
        // inserted, which is a safe, obvious failure mode for the admin.
        console.error("Image upload failed", err);
      }
    };
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: ["1", "2", "3", false] }],
          ["bold", "italic", "underline", "link"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["image"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
    }),
    []
  );

  if (!isOpen) return null;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const isValid = form.subject && form.bodyHtml && form.senderName && form.replyToEmail;

  const handleSubmit = (action) => {
    if (!isValid) return;
    onSubmit(
      {
        subject: form.subject,
        preheaderText: form.preheaderText || undefined,
        bodyHtml: form.bodyHtml,
        senderName: form.senderName,
        replyToEmail: form.replyToEmail,
      },
      action,
      action === "sendTest" ? testEmail : undefined
    );
  };

  const isEdit = !!campaign;
  // Only a still-unsent draft (or one that failed outright) can be sent -
  // matches the same status gate the row-level "Send" dropdown item uses.
  const canSend = isEdit && (campaign.status === "DRAFT" || campaign.status === "FAILED");

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden mx-4 w-full max-w-3xl bg-white rounded-2xl shadow-2xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Product Update" : "New Product Update"}
          </h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <section className="space-y-3">
            <div>
              <input
                ref={subjectRef}
                type="text"
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
              <VariableChips onInsert={(token) => insertVariableIntoField("subject", subjectRef, token)} />
            </div>
            <div>
              <input
                ref={preheaderRef}
                type="text"
                placeholder="Preheader (the preview text shown next to the subject in an inbox)"
                value={form.preheaderText}
                onChange={(e) => handleChange("preheaderText", e.target.value)}
                className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <VariableChips
                onInsert={(token) => insertVariableIntoField("preheaderText", preheaderRef, token)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Sender Name, e.g. Novacrust Team"
                value={form.senderName}
                onChange={(e) => handleChange("senderName", e.target.value)}
                className="px-4 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="email"
                placeholder="Reply-To email"
                value={form.replyToEmail}
                onChange={(e) => handleChange("replyToEmail", e.target.value)}
                className="px-4 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </section>

          <section>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs text-gray-500">Body</label>
              <VariableChips onInsert={insertVariableIntoBody} />
            </div>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={form.bodyHtml}
              onChange={(html) => handleChange("bodyHtml", html)}
              modules={quillModules}
            />
          </section>

          <section className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="email"
              placeholder="Send a test to this address first"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => handleSubmit("sendTest")}
              disabled={!isValid || !testEmail || isLoading}
              className="px-4 py-2 text-sm font-bold text-[#013941] bg-white rounded-lg border-2 border-[#013941] disabled:opacity-50"
            >
              Send Test
            </button>
          </section>
        </form>

        <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white rounded-xl border-2 border-gray-200 transition-all hover:bg-gray-50 active:scale-95"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSubmit("save")}
            disabled={!isValid || isLoading}
            className={`px-5 py-2.5 text-sm font-bold text-white bg-[#013941] rounded-xl transition-all active:scale-95 ${
              !isValid || isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#012B36]"
            }`}
          >
            {isLoading ? "Saving..." : isEdit ? "Save Changes" : "Save Draft"}
          </button>
          {canSend && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Send this update to all eligible recipients now?")) {
                  handleSubmit("send");
                }
              }}
              disabled={!isValid || isLoading}
              className={`px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl transition-all active:scale-95 ${
                !isValid || isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-700"
              }`}
            >
              {isLoading ? "Sending..." : "Send"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductUpdateFormModal;
